import { z } from 'zod';

// Offering types for the application
export const OfferingTypes = [
  'course',
  'software', 
  'service',
  'consulting',
  'marketing',
  'operations',
  'other',
] as const;

export type OfferingType = typeof OfferingTypes[number];

// Sale methods
export const SaleMethods = ['buy_direct', 'consultation', 'hybrid'] as const;
export type SaleMethod = typeof SaleMethods[number];

// Production ranges for agent targeting
export const ProductionRanges = ['new_under_5', '5_to_15', '15_to_30', '30_plus'] as const;
export type ProductionRange = typeof ProductionRanges[number];

// Target agent types
export const TargetAgentTypes = ['individual', 'teams', 'brokerages'] as const;
export type TargetAgentType = typeof TargetAgentTypes[number];

// Outcome tracking status
export const TracksOutcomesOptions = ['yes', 'somewhat', 'not_yet'] as const;
export type TracksOutcomes = typeof TracksOutcomesOptions[number];

export const ZVendorApplication = z.object({
  // Step 1: Offering Type
  offering_type: z.enum(OfferingTypes).optional(),
  
  // Step 2: Sale Method
  sale_method: z.enum(SaleMethods).optional(),
  
  // Step 3: Your Ideal Agent (Fingerprint)
  production_ranges: z.array(z.enum(ProductionRanges)).optional(),
  target_agent_types: z.array(z.enum(TargetAgentTypes)).optional(),
  primary_pain: z.string().max(300).optional(),
  best_customer_description: z.string().optional(),
  
  // Step 4: Proof & Tracking
  tracks_outcomes: z.enum(TracksOutcomesOptions).optional(),
  
  // Step 5: Pricing
  public_price: z.number().min(0).optional(),
  circle_pro_price: z.number().min(0).optional(),
  base_price_note: z.string().optional(), // "Starting at" indicator
  
  // Step 6: Commercial Terms (required at final step)
  commercial_terms_acknowledged: z.boolean().optional(),
  
  // Step 7: Categories & Discovery
  product_category: z.string().min(1, 'Category is required'),
  secondary_categories: z.array(z.string()).optional(),
  
  // Step 8: Listing Builder
  what_this_is: z.string().optional(),
  who_its_for: z.string().optional(),
  what_success_looks_like: z.string().optional(),
  what_happens_after_purchase: z.string().optional(),
  
  // Step 9: Partner Call
  partner_call_booked: z.boolean().optional(),
  partner_call_datetime: z.string().optional(),
  
  // Legacy fields (still used but reorganized)
  company_name: z.string().min(2, 'Company name is required'),
  company_website: z.string().url('Must be a valid URL'),
  company_year_founded: z.number().min(1900).max(new Date().getFullYear()).optional(),
  company_size: z.string().optional(),
  company_stage: z.string().optional(),
  company_location: z.string().optional(),
  company_linkedin: z.string().url().optional().or(z.literal('')),
  
  // Contact Person
  contact_name: z.string().min(2, 'Contact name is required'),
  contact_title: z.string().min(2, 'Title is required'),
  contact_email: z.string().email('Must be a valid email'),
  contact_phone: z.string().optional(),
  contact_linkedin: z.string().url().optional().or(z.literal('')),
  
  // Product Info
  product_name: z.string().min(2, 'Product name is required'),
  service_type: z.enum(['product', 'service', 'education']),
  product_brief_description: z.string().min(20, 'Brief description must be at least 20 characters'),
  product_detailed_description: z.string().min(100, 'Detailed description must be at least 100 characters'),
  product_website: z.string().url('Must be a valid URL'),
  product_demo_url: z.string().url().optional().or(z.literal('')),
  target_audience: z.string().min(10, 'Target audience is required'),
  problem_solved: z.string().min(20, 'Problem solved description is required'),
  
  // Pricing (legacy - keep for backwards compatibility)
  current_price: z.number().min(0, 'Price must be positive'),
  proposed_deal_price: z.number().min(0, 'Deal price must be positive'),
  pricing_model: z.string().min(5, 'Pricing model is required'),
  plan_count: z.number().optional(),
  deal_includes: z.string().min(10, 'Deal inclusions are required'),
  
  // Assets (URLs after upload)
  logo_url: z.string().optional(),
  screenshots: z.array(z.string().url()).optional(),
  demo_video_url: z.string().url().optional().or(z.literal('')),
  testimonials: z.array(z.object({
    quote: z.string(),
    author: z.string(),
    role: z.string().optional(),
  })).optional(),
  
  // Technical
  key_features: z.array(z.string().min(5)).min(3, 'At least 3 key features required'),
  integrations: z.array(z.string()).optional(),
  technical_requirements: z.string().optional(),
  setup_time_estimate: z.string().optional(),
  
  // Business Metrics
  customer_count: z.number().optional(),
  monthly_revenue: z.number().optional(),
  average_rating: z.number().min(0).max(5).optional(),
  prior_ltd_experience: z.boolean().optional(),
  
  // Deal Objectives
  primary_goal: z.string().min(10, 'Primary goal is required'),
  expected_units: z.number().optional(),
  campaign_duration: z.string().optional(),
  special_requirements: z.string().optional(),
  
  // Legal
  terms_url: z.string().url('Terms URL is required'),
  privacy_url: z.string().url('Privacy URL is required'),
  agreed_to_terms: z.boolean().refine(val => val === true, 'You must agree to the terms'),
  consent_to_processing: z.boolean().refine(val => val === true, 'You must consent to data processing'),
});

export type VendorApplication = z.infer<typeof ZVendorApplication>;

export interface VendorApplicationRow extends VendorApplication {
  id: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'needs_info';
  reviewed_at?: string;
  reviewed_by?: string;
  review_notes?: string;
}

// Labels for UI display
export const OfferingTypeLabels: Record<OfferingType, string> = {
  course: 'Course / Education',
  software: 'Software / Tool',
  service: 'Service (Done-for-you)',
  consulting: 'Consulting / Coaching',
  marketing: 'Marketing / Creative',
  operations: 'Operations / Back Office',
  other: 'Other',
};

export const SaleMethodLabels: Record<SaleMethod, string> = {
  buy_direct: 'Buy directly on Circle (off-the-shelf)',
  consultation: 'Consultation / Custom engagement',
  hybrid: 'Hybrid (base product + consultation)',
};

export const ProductionRangeLabels: Record<ProductionRange, string> = {
  new_under_5: 'New / <5 deals',
  '5_to_15': '5–15 deals',
  '15_to_30': '15–30 deals',
  '30_plus': '30+ deals',
};

export const TargetAgentTypeLabels: Record<TargetAgentType, string> = {
  individual: 'Individual Agents',
  teams: 'Teams',
  brokerages: 'Brokerages',
};

export const TracksOutcomesLabels: Record<TracksOutcomes, string> = {
  yes: 'Yes (conversion, retention, revenue)',
  somewhat: 'Somewhat',
  not_yet: 'Not yet',
};
